package ai.jessie.web.theme

import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.extendedBy
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.until

val CalendarToolbarTitleStyle = CssStyle.base { css("font-size" to "15px") }

val CalendarToolbarStyle = CssStyle.base {
    css(
        "display" to "flex",
        "align-items" to "center",
        "justify-content" to "space-between",
        "flex-wrap" to "wrap",
        "gap" to "12px",
        "padding" to "16px 20px",
        "border-bottom" to "1px solid ${LineVar.value()}",
    )
}

// One month fills the card that `02-Main.png` shares among three, so its cells grow from MD on.
val CalendarMonthStyle = CssStyle {
    base {
        css(
            "display" to "grid",
            "grid-template-columns" to "repeat(8, 44px)",
            "width" to "max-content",
            "gap" to "8px",
            "max-width" to "100%",
            "margin" to "12px auto 24px",
            "align-items" to "center",
            "text-align" to "center",
        )
    }
    cssRule(" > [data-date]") { css("width" to "44px", "height" to "44px", "font-size" to "14px") }
    until(Breakpoint.MD) { css("grid-template-columns" to "repeat(8, 34px)", "gap" to "6px") }
    cssRule((Breakpoint.ZERO..<Breakpoint.MD).toCSSMediaQuery(), " > [data-date]") {
        css("width" to "34px", "height" to "34px", "font-size" to "13px")
    }
}

val CalendarMonthTitleStyle = CssStyle.base {
    css("text-align" to "center", "padding" to "24px 0 12px", "font-size" to "15px")
}

val CalendarDayControlStyle = CssStyle {
    base {
        css("cursor" to "pointer")
    }
    cssRule(":focus-visible") {
        css("outline" to "2px solid ${PrimaryVar.value()}", "outline-offset" to "2px")
    }
}

val CalendarSumStyle = CssStyle.base {
    css("font-size" to "11px", "color" to InkMutedVar.value())
}

val CalendarSummaryStyle = CssStyle {
    base {
        css(
            "padding" to "12px 20px",
            "display" to "flex",
            "flex-direction" to "column",
            "gap" to "4px",
            "font-size" to "13px",
            "color" to InkMutedVar.value(),
        )
    }
    cssRule(" p") { css("margin" to "0") }
}

val CalendarScrollStyle = CssStyle.base { css("overflow-x" to "auto", "max-width" to "100%") }

val CalendarWeekStyle = CssStyle.base {
    css(
        "display" to "grid",
        "grid-template-columns" to "repeat(7, minmax(100px, 1fr))",
        "min-width" to "700px",
        "padding" to "0",
        "margin" to "0",
        "list-style" to "none",
    )
}

val CalendarWeekDayStyle = CssStyle {
    base {
        css(
            "min-width" to "0",
            "border-right" to "1px solid ${LineSoftVar.value()}",
            "background" to SurfaceMutedVar.value(),
        )
    }
    cssRule(" h3") { css("font-size" to "14px", "margin" to "0") }
    cssRule("[aria-current='date'] h3") {
        css(
            "background" to PrimaryVar.value(),
            "color" to SurfaceVar.value(),
            "border-radius" to JessieTokens.RADIUS_PILL,
        )
    }
}

val CalendarWeekHeadStyle = CssStyle.base {
    css(
        "text-align" to "center",
        "padding" to "12px 6px",
        "background" to SurfaceVar.value(),
        "border-bottom" to "1px solid ${LineVar.value()}",
    )
}

val CalendarAllDayStyle = CssStyle.base {
    css(
        "display" to "flex",
        "flex-direction" to "column",
        "gap" to "6px",
        "padding" to "8px",
        "min-height" to "100px",
    )
}

val CalendarEventStyle = CssStyle {
    base {
        css(
            "display" to "flex",
            "flex-direction" to "column",
            "gap" to "3px",
            "padding" to "8px",
            "border-radius" to "7px",
            "font-size" to "12px",
            "overflow-wrap" to "anywhere",
            "background" to "#EEEDE8",
            "color" to "#3D4250",
            "border-left" to "3px solid #9AA0AE",
        )
    }
    cssRule("[data-project-colour='0']") {
        css("background" to "#E6EBFA", "color" to "#1E3CA8", "border-left-color" to "#2B4FD1")
    }
    cssRule("[data-project-colour='1']") {
        css("background" to "#F0E8FA", "color" to "#4E2C86", "border-left-color" to "#6B3FB5")
    }
}

val CalendarTableStyle = CssStyle.base { css("min-width" to "660px") }
val CalendarTableRowStyle = CssStyle.base {
    css("grid-template-columns" to "1fr 1fr 1.5fr 80px 110px")
}

// Work list (`docs/design/screens/04-Stunden.png`): project, position, description, hours, action.
val WorkTableStyle = CssStyle.base { css("min-width" to "720px") }
val WorkTableRowStyle = CssStyle.base {
    css("grid-template-columns" to "1.2fr 1.5fr 2fr 90px 110px")
}

// Customer list (`docs/design/screens/15-Firmen.png`): type icon, name, place, status, chevron.
val CustomerTableStyle = CssStyle.base { css("min-width" to "640px") }
val CustomerTableRowStyle = CssStyle.base {
    css("grid-template-columns" to "24px minmax(0, 1fr) 200px 120px 32px")
}

// VAT versions (`docs/design/screens/23-EinstellungenMwst.png`): name, rate, valid from/to,
// current.
val VatTableStyle = CssStyle.base { css("min-width" to "560px") }
val VatTableRowStyle = CssStyle.base {
    css("grid-template-columns" to "minmax(0, 1fr) 90px 120px 120px 80px")
}

// Project list (`docs/design/screens/12-Projekte.png`): project with its positions, customer,
// status, position count, chevron.
val ProjectTableStyle = CssStyle.base { css("min-width" to "680px") }
val ProjectTableRowStyle = CssStyle.base {
    css(
        "grid-template-columns" to "minmax(0, 1fr) 180px 110px 90px 32px",
        "padding-top" to "10px",
        "padding-bottom" to "10px",
    )
}

// Invoice list (no artboard; follows `12-Projekte.png`): number, issue date, recipient, status,
// gross total, chevron.
val InvoiceTableStyle = CssStyle.base { css("min-width" to "680px") }
val InvoiceTableRowStyle = CssStyle.base {
    css("grid-template-columns" to "150px 130px minmax(0, 1fr) 110px 140px 32px")
}

// Invoice positions (`06-Rechnung.png`): position, quantity, price, VAT rate, net, VAT, gross and
// an optional action.
val InvoiceLineTableStyle = CssStyle.base { css("min-width" to "860px") }
val InvoiceLineTableRowStyle = CssStyle.base {
    css(
        "grid-template-columns" to "minmax(200px, 1fr) 80px 90px 120px 100px 90px 100px auto",
        "padding-top" to "10px",
        "padding-bottom" to "10px",
    )
}

/** Header cell above a [TableNumStyle] column. */
val TableNumHeadStyle = CssStyle.base { css("text-align" to "right") }

/**
 * The server's totals below the positions (`06-Rechnung.png`), right-aligned; the last pair is the
 * total. Apply to a `<dl>`.
 */
val InvoiceTotalsStyle = CssStyle {
    base {
        css(
            "display" to "grid",
            "grid-template-columns" to "auto auto",
            "justify-content" to "end",
            "gap" to "8px 32px",
            "margin" to "0",
            "padding" to "16px 20px 20px",
            "border-top" to "1px solid ${LineVar.value()}",
        )
    }
    cssRule(" dt") { css("color" to Ink3Var.value()) }
    cssRule(" dd") {
        css(
            "margin" to "0",
            "text-align" to "right",
            "font-family" to JessieTokens.FONT_NUM,
            "font-variant-numeric" to "tabular-nums",
        )
    }
    cssRule(" dt:last-of-type") {
        css("color" to InkVar.value(), "font-weight" to "600", "padding-top" to "8px")
    }
    cssRule(" dd:last-of-type") { css("font-weight" to "600", "padding-top" to "8px") }
}

/** Invoice detail: record and positions beside the status and PDF cards; stacked below 900 px. */
val InvoiceLayoutStyle = CssStyle {
    base {
        css(
            "display" to "grid",
            "grid-template-columns" to "minmax(0, 1fr) 360px",
            "gap" to "20px",
            "align-items" to "start",
        )
    }
    until(Breakpoint.LG) { css("grid-template-columns" to "minmax(0, 1fr)") }
}

/**
 * Document inbox beside the selected document's card (`07-Dokumente.png`); stacked below 900 px.
 */
val DocumentLayoutStyle = CssStyle {
    base {
        css(
            "display" to "grid",
            "grid-template-columns" to "minmax(0, 1fr) minmax(0, 1fr)",
            "gap" to "20px",
            "align-items" to "start",
        )
    }
    until(Breakpoint.LG) { css("grid-template-columns" to "minmax(0, 1fr)") }
}

val DocumentListStyle = CssStyle.base {
    css("list-style" to "none", "margin" to "0", "padding" to "0")
}

/** DocumentListItem (`components.md`): thumbnail, title and meta; the selected item is tinted. */
val DocumentItemStyle = CssStyle {
    base {
        css(
            "display" to "flex",
            "align-items" to "center",
            "gap" to "14px",
            "padding" to "12px 20px",
            "border-top" to "1px solid ${LineSoftVar.value()}",
            "color" to InkVar.value(),
            "text-decoration" to "none",
        )
    }
    cssRule(":hover") { css("background" to SurfaceMutedVar.value()) }
    cssRule("[aria-current=\"page\"]") { css("background" to PrimaryTintSoftVar.value()) }
}

val DocumentThumbStyle = CssStyle.base {
    css(
        "flex" to "none",
        "display" to "flex",
        "align-items" to "flex-end",
        "justify-content" to "center",
        "width" to "38px",
        "height" to "46px",
        "padding-bottom" to "6px",
        "box-sizing" to "border-box",
        "border" to "1px solid ${LineVar.value()}",
        "border-radius" to "6px",
        "background" to SurfaceMutedVar.value(),
        "font-size" to "10px",
        "font-weight" to "600",
        "color" to InkMutedVar.value(),
    )
}

val DocumentItemTextStyle = CssStyle.base {
    css("display" to "flex", "flex-direction" to "column", "gap" to "2px", "min-width" to "0")
}

val DocumentItemTitleStyle = CssStyle.base {
    css(
        "font-size" to "14px",
        "font-weight" to "600",
        "overflow" to "hidden",
        "text-overflow" to "ellipsis",
        "white-space" to "nowrap",
    )
}

val DocumentItemMetaStyle = CssStyle.base {
    css("font-size" to "12px", "color" to InkMutedVar.value())
}

/** Transfer progress: a thin track with the bar inside. */
val DocumentProgressStyle = CssStyle.base {
    css(
        "height" to "6px",
        "border-radius" to JessieTokens.RADIUS_PILL,
        "background" to SurfaceSunkenVar.value(),
        "overflow" to "hidden",
    )
}

val DocumentProgressBarStyle = CssStyle.base {
    css("height" to "100%", "background" to PrimaryVar.value())
}

/**
 * ProgressSteps (`components.md`): steps in order, each a 22 px circle and a label. Apply to an
 * `<ol>`; mark finished steps with `data-done` and the current one with `aria-current="step"`.
 */
val ProgressStepsStyle = CssStyle {
    base {
        css(
            "list-style" to "none",
            "margin" to "0",
            "padding" to "0",
            "display" to "flex",
            "flex-wrap" to "wrap",
            "gap" to "8px 16px",
            "font-size" to "13px",
            "color" to InkMutedVar.value(),
        )
    }
    cssRule(" > li") { css("display" to "inline-flex", "align-items" to "center", "gap" to "8px") }
    // 28 px connector before every step but the first; a wrapped line starts with its connector.
    cssRule(" > li + li::before") {
        css(
            "content" to "\"\"",
            "width" to "28px",
            "height" to "1px",
            "margin-right" to "8px",
            "background" to LineVar.value(),
        )
    }
    cssRule(" > li > span:first-child") {
        css(
            "width" to "22px",
            "height" to "22px",
            "border-radius" to JessieTokens.RADIUS_PILL,
            "display" to "inline-flex",
            "align-items" to "center",
            "justify-content" to "center",
            "background" to SurfaceSunkenVar.value(),
            "color" to Ink3Var.value(),
            "font-size" to "12px",
            "font-weight" to "600",
        )
    }
    cssRule(" > li[data-done] > span:first-child") {
        css("background" to HoursVar.value(), "color" to "white")
    }
    cssRule(" > li[aria-current]") { css("color" to InkVar.value(), "font-weight" to "600") }
    cssRule(" > li[aria-current] > span:first-child") {
        css("background" to PrimaryVar.value(), "color" to "white")
    }
}

/**
 * Stat bar of a detail page (`05-Projekt.png`): a card with label/value cells side by side, a
 * vertical line between them; the cells wrap on narrow screens. Apply to a `<dl>`.
 */
val StatBarStyle = CssStyle {
    base {
        css(
            "display" to "flex",
            "flex-wrap" to "wrap",
            "gap" to "16px 0",
            "margin" to "0",
            "padding" to "20px 0",
        )
    }
    cssRule(" > div") {
        css(
            "flex" to "1 1 180px",
            "min-width" to "0",
            "padding" to "0 24px",
            "display" to "flex",
            "flex-direction" to "column",
            "gap" to "6px",
        )
    }
    cssRule(" > div + div") { css("border-left" to "1px solid ${LineVar.value()}") }
    cssRule(" dt") {
        css(
            "font-size" to "12px",
            "font-weight" to "600",
            "letter-spacing" to "0.05em",
            "text-transform" to "uppercase",
            "color" to InkMutedVar.value(),
        )
    }
    cssRule(" dd") {
        css(
            "margin" to "0",
            "display" to "flex",
            "flex-wrap" to "wrap",
            "align-items" to "center",
            "gap" to "6px",
            "overflow-wrap" to "anywhere",
        )
    }
    until(Breakpoint.LG) { css("flex-direction" to "column") }
    // Stacked cells take their content height, not the 180 px row basis.
    cssRule((Breakpoint.ZERO..<Breakpoint.LG).toCSSMediaQuery(), " > div") {
        css("flex" to "none")
    }
    cssRule((Breakpoint.ZERO..<Breakpoint.LG).toCSSMediaQuery(), " > div + div") {
        css("border-left" to "0")
    }
}

/** A large stat number in Dank Mono (24/500). */
val StatValueStyle = CssStyle.base {
    css(
        "font-family" to JessieTokens.FONT_NUM,
        "font-size" to "24px",
        "font-weight" to "500",
        "font-variant-numeric" to "tabular-nums",
    )
}

/** Position row cards below each other (`05-Projekt.png`, Projektstruktur). */
val PositionListStyle = CssStyle.base {
    css(
        "list-style" to "none",
        "margin" to "0",
        "padding" to "0",
        "display" to "flex",
        "flex-direction" to "column",
        "gap" to "12px",
    )
}

/** One position: kind and name, stat columns, actions; stacked on narrow screens. */
val PositionCardStyle = CardStyle.extendedBy {
    base {
        css(
            "display" to "grid",
            "grid-template-columns" to "minmax(0, 1.4fr) auto minmax(0, 1fr)",
            "align-items" to "center",
            "gap" to "16px 32px",
            "padding" to "18px 24px",
        )
    }
    until(Breakpoint.LG) { css("grid-template-columns" to "minmax(0, 1fr)") }
}

/** Kind label (Aufwand / Festpreis, the first span) and badges above the position name. */
val PositionKindStyle = CssStyle {
    base {
        css("display" to "flex", "flex-wrap" to "wrap", "align-items" to "center", "gap" to "8px")
    }
    cssRule(" > span:first-child") {
        css(
            "font-size" to "12px",
            "font-weight" to "600",
            "letter-spacing" to "0.05em",
            "text-transform" to "uppercase",
            "color" to PrimaryVar.value(),
        )
    }
}

val PositionNameStyle = CssStyle.base {
    css("margin" to "4px 0 0", "font-size" to "16px", "font-weight" to "600")
}

/** The stat columns of a position card, at least 110 px each (`components.md`, PositionCard). */
val PositionStatsStyle = CssStyle {
    base {
        css(
            "display" to "grid",
            "grid-template-columns" to "repeat(2, minmax(110px, max-content))",
            "gap" to "4px 16px",
            "margin" to "0",
        )
    }
    cssRule(" dt") {
        css(
            "grid-row" to "1",
            "font-size" to "12px",
            "font-weight" to "600",
            "letter-spacing" to "0.05em",
            "text-transform" to "uppercase",
            "color" to InkMutedVar.value(),
        )
    }
    cssRule(" dd") {
        css(
            "grid-row" to "2",
            "margin" to "0",
            "font-family" to JessieTokens.FONT_NUM,
            "font-variant-numeric" to "tabular-nums",
            "overflow-wrap" to "anywhere",
        )
    }
}

/** A cell spanning the whole row, e.g. the letter of an alphabetical group. */
val TableSpanStyle = CssStyle.base { css("grid-column" to "1 / -1") }

/** Muted icon cell, e.g. a record type glyph or the trailing chevron link. */
val TableIconStyle = CssStyle.base {
    css("display" to "inline-flex", "align-items" to "center", "color" to InkMutedVar.value())
}

/** People with avatar, name and a muted detail line (`16-Firma.png`, Personen card). */
val PersonListStyle = CssStyle.base {
    css(
        "list-style" to "none",
        "margin" to "0",
        "padding" to "0",
        "display" to "flex",
        "flex-direction" to "column",
        "gap" to "14px",
    )
}

val PersonStyle = CssStyle.base {
    css("display" to "flex", "align-items" to "center", "gap" to "12px")
}

/** Two columns of member and invitation cards (`20-EinstellungenTeam.png`); one below 900 px. */
val MemberGridStyle = CssStyle {
    base {
        css(
            "list-style" to "none",
            "margin" to "0",
            "padding" to "0",
            "display" to "grid",
            "grid-template-columns" to "repeat(2, minmax(0, 1fr))",
            "gap" to "16px",
        )
    }
    until(Breakpoint.LG) { css("grid-template-columns" to "minmax(0, 1fr)") }
}

/** One member or invitation: avatar, name over e-mail and badges; details and actions below. */
val MemberCardStyle = CardStyle.extendedByBase {
    css(
        "display" to "flex",
        "flex-direction" to "column",
        "gap" to "12px",
        "padding" to "18px 20px",
        "min-width" to "0",
    )
}

/** A pending invitation: the member card with a dashed border. */
val PendingCardStyle = MemberCardStyle.extendedByBase { css("border-style" to "dashed") }

/** Avatar, name and badges; the badges move below the name when the card is narrow. */
val MemberHeadStyle = CssStyle.base {
    css("display" to "flex", "flex-wrap" to "wrap", "align-items" to "center", "gap" to "12px")
}

/** Name over the muted e-mail; takes the space between avatar and badges. */
val MemberNameStyle = CssStyle.base {
    css(
        "display" to "flex",
        "flex-direction" to "column",
        "flex" to "1 1 10rem",
        "min-width" to "0",
        "overflow-wrap" to "anywhere",
    )
}

/** The card's actions; long labels wrap inside their button instead of widening the card. */
val MemberActionsStyle = CssStyle {
    base { css("display" to "flex", "flex-wrap" to "wrap", "gap" to "8px") }
    cssRule(" button") {
        css(
            "white-space" to "normal",
            "height" to "auto",
            "min-height" to JessieTokens.CONTROL_HEIGHT_SM,
            "padding-top" to "4px",
            "padding-bottom" to "4px",
            "text-align" to "left",
        )
    }
}

/** Emphasised text cell, e.g. the project name of a work row. */
val TableStrongStyle = CssStyle.base { css("font-weight" to "600") }

/** Label/value pairs of one record: a 120 px muted term column, values beside it. */
val DefinitionsStyle = CssStyle {
    base {
        css(
            "display" to "grid",
            "grid-template-columns" to "120px minmax(0, 1fr)",
            "gap" to "8px 16px",
            "align-items" to "baseline",
            "margin" to "0",
            "font-size" to "14px",
        )
    }
    cssRule(" dt") { css("color" to InkMutedVar.value(), "overflow-wrap" to "anywhere") }
    cssRule(" dd") { css("margin" to "0", "overflow-wrap" to "anywhere") }
    // Narrow screens stack each term above its value instead of squeezing the value column.
    until(Breakpoint.LG) { css("grid-template-columns" to "minmax(0, 1fr)", "gap" to "4px 0") }
    cssRule((Breakpoint.ZERO..<Breakpoint.LG).toCSSMediaQuery(), " dd:not(:last-child)") {
        css("margin-bottom" to "10px")
    }
}

// Tables: CSS grid rows; each table sets its own column template inline.

val TableStyle = CssStyle.base {
    css("display" to "flex", "flex-direction" to "column", "overflow" to "hidden")
}

sealed interface TableRowKind : ComponentKind

val TableRowStyle =
    CssStyle.base<TableRowKind> {
        css(
            "display" to "grid",
            "gap" to "12px",
            "align-items" to "center",
            "padding" to "0 20px",
            "min-height" to JessieTokens.ROW_HEIGHT,
            "border-top" to "1px solid ${LineSoftVar.value()}",
            "font-size" to "14px",
        )
    }

val HeadTableRowVariant = TableRowStyle.addVariantBase {
    css(
        "min-height" to "40px",
        "border-top" to "0",
        "font-size" to "12px",
        "font-weight" to "600",
        "letter-spacing" to "0.04em",
        "text-transform" to "uppercase",
        "color" to InkMutedVar.value(),
    )
}

val GroupTableRowVariant = TableRowStyle.addVariantBase {
    css(
        "min-height" to "36px",
        "background" to SurfaceMutedVar.value(),
        "border-top-color" to LineVar.value(),
        "font-size" to "12px",
        "font-weight" to "600",
        "color" to Ink3Var.value(),
    )
}

val TotalTableRowVariant = TableRowStyle.addVariantBase {
    css(
        "background" to GroundVar.value(),
        "border-top-color" to LineVar.value(),
        "font-weight" to "600",
        "min-height" to "52px",
    )
}

/** Right-aligned number cell in Dank Mono. */
val TableNumStyle = CssStyle.base {
    css(
        "text-align" to "right",
        "font-family" to JessieTokens.FONT_NUM,
        "font-weight" to NumWeightVar.value(),
        "font-variant-numeric" to "tabular-nums",
    )
}

// Calendar heat-map cells

sealed interface DayKind : ComponentKind

val DayStyle =
    CssStyle.base<DayKind> {
        css(
            "width" to "34px",
            "background" to "transparent",
            "border" to "0",
            "padding" to "0",
            "height" to "34px",
            "border-radius" to JessieTokens.RADIUS_CELL,
            "display" to "inline-flex",
            "align-items" to "center",
            "justify-content" to "center",
            "font-size" to "13px",
            "font-weight" to "500",
            "box-sizing" to "border-box",
        )
    }

val Heat0DayVariant = DayStyle.addVariantBase { css("color" to InkPlaceholderVar.value()) }

val Heat1DayVariant = DayStyle.addVariantBase {
    css("background" to Heat1Var.value(), "color" to Heat1InkVar.value())
}

val Heat2DayVariant = DayStyle.addVariantBase {
    css("background" to Heat2Var.value(), "color" to Heat2InkVar.value())
}

val Heat3DayVariant = DayStyle.addVariantBase {
    css("background" to Heat3Var.value(), "color" to Heat3InkVar.value())
}

val Heat4DayVariant = DayStyle.addVariantBase {
    css("background" to Heat4Var.value(), "color" to Heat4InkVar.value())
}

val Heat5DayVariant = DayStyle.addVariantBase {
    css("background" to Heat5Var.value(), "color" to Heat5InkVar.value())
}

val TodayDayVariant = DayStyle.addVariantBase {
    css(
        "border" to "2px solid ${PrimaryVar.value()}",
        "color" to PrimaryVar.value(),
        "font-weight" to "700",
    )
}

/** Heat-map steps 0–5 (0 h · <4 h · 4–6 h · 6–8 h · 8–10 h · >10 h), by index. */
val HeatDayVariants =
    listOf(
        Heat0DayVariant,
        Heat1DayVariant,
        Heat2DayVariant,
        Heat3DayVariant,
        Heat4DayVariant,
        Heat5DayVariant,
    )
